<template>
  <DxPopup
    :visible="visible"
    :show-title="true"
    :title="$t('scale.weighing_on_scale.recipient_warehouse')"
    :width="900"
    :height="620"
    :drag-enabled="true"
    :close-on-outside-click="false"
    content-template="popupContent"
    @hiding="onCancel"
  >
    <template #popupContent>
    <div class="warehouse-split-popup-content">
      <div class="warehouse-split-popup-header">
        <span class="warehouse-split-popup-text">Total: {{ formattedTotal }} kg</span>
      </div>
      <div class="warehouse-split-popup-grid-wrap">
      <DxDataGrid
        :data-source="rows"
        key-expr="row_key"
        :show-borders="true"
        :column-auto-width="true"
        :allow-column-resizing="true"
        :row-alternation-enabled="true"
        :element-attr="gridAttributes"
        height="100%"
        @cell-prepared="onCellPrepared"
      >
        <DxEditing :allow-updating="true" mode="cell" />
        <DxColumn data-field="legal_entity_name" :allow-editing="false" :caption="$t('scale.weighing_on_scale.legal_entity')" />
        <DxColumn data-field="warehouse_name" :allow-editing="false" :caption="$t('common.warehouse')" />
        <DxColumn data-field="parcel_names" :allow-editing="false" :caption="$t('works.parcels.parcels')" />
        <DxColumn data-field="total_surface" data-type="number" :allow-editing="false" :format="numberFormat" :caption="$t('works.parcels.total_surface') + ' (ha)'" />
        <DxColumn data-field="quantity_kg" data-type="number" :format="numberFormat" :caption="$t('scale.weighing_on_scale.quantity_kg')" />
      </DxDataGrid>
      </div>
      <div class="warehouse-split-popup-actions">
        <SmagriaButton :label="$t('common.cancel')" :action="onCancel" :customClass="'secondary border'" />
        <SmagriaButton :label="$t('common.save')" :action="onSave" :customClass="'primary'" />
      </div>
    </div>
    </template>
  </DxPopup>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import { DxDataGrid, DxColumn, DxEditing } from "devextreme-vue/data-grid";
import { errorMessage } from "@/helpers/common";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import { getQuantityFormatter } from "@/helpers/stockHelper";

export default {
  name: "WarehouseSplitPopup",
  props: {},
  data() {
    return {
      visible: false,
      rows: [],
      targetTotal: 0,
      expectedLegalEntityUuids: [],
      promiseResolve: null,
      gridAttributes: {
        class: "smagria-grid warehouse-split-grid",
      },
    };
  },
  components: {
    DxPopup,
    DxDataGrid,
    DxColumn,
    DxEditing,
    SmagriaButton,
  },
  computed: {
    numberFormat() {
      return getQuantityFormatter();
    },
    formattedTotal() {
      const decimals = this.$store.getters['settings/getQuantityDecimalPlaces'] ?? 3;
      return Number(this.targetTotal).toFixed(decimals);
    },
  },
  methods: {
    open({ rows = [], targetTotal = 0, expectedLegalEntityUuids = [] } = {}) {
      return new Promise((resolve) => {
        this.rows = Array.isArray(rows) ? rows.map((row) => ({ ...row })) : [];
        this.targetTotal = Number(targetTotal) || 0;
        this.expectedLegalEntityUuids = Array.isArray(expectedLegalEntityUuids) ? [...expectedLegalEntityUuids] : [];
        this.promiseResolve = resolve;
        this.visible = true;
      });
    },
    closeWith(result) {
      this.visible = false;
      if (this.promiseResolve) {
        this.promiseResolve(result);
      }
      this.promiseResolve = null;
      this.rows = [];
      this.targetTotal = 0;
      this.expectedLegalEntityUuids = [];
    },
    onCellPrepared(e) {
      if (e.rowType === "data" && e.column.allowEditing) {
        e.cellElement.classList.add("editable-cell-yellow");
      }
    },
    onCancel() {
      this.closeWith(null);
    },
    onSave() {
      const validation = this.validateRows(this.rows, this.targetTotal, this.expectedLegalEntityUuids);
      if (validation.error) {
        errorMessage(this, validation.message || this.$t("fill_out_error"));
        return;
      }

      const payload = this.rows.map((row) => ({
        ...row,
        quantity_kg: Number(row.quantity_kg) || 0,
      }));

      this.closeWith(payload);
    },
    roundToDecimals(value, decimals) {
      if (typeof value !== "number") value = Number(value) || 0;
      return Number(value.toFixed(decimals));
    },
    validateRows(rows, targetTotal, expectedLegalEntityUuids = []) {
      if (!Array.isArray(rows) || rows.length === 0) {
        return {
          error: true,
          message: this.$t("fill_out_error"),
        };
      }

      const hasZeroQuantity = rows.some((row) => (Number(row.quantity_kg) || 0) <= 0);
      if (hasZeroQuantity) {
        return {
          error: true,
          message: this.$t("scale.weighing_on_scale.error_split_row_zero_quantity"),
        };
      }

      const decimalPlaces = this.$store.getters['settings/getQuantityDecimalPlaces'] ?? 3;
      const rowsTotal = rows.reduce((sum, row) => sum + (Number(row.quantity_kg) || 0), 0);
      const roundedRowsTotal = this.roundToDecimals(rowsTotal, decimalPlaces);
      const roundedTarget = this.roundToDecimals(Number(targetTotal) || 0, decimalPlaces);

      if (roundedRowsTotal !== roundedTarget) {
        return {
          error: true,
          message: this.$t("scale.weighing_on_scale.error_split_sum_mismatch"),
        };
      }

      /*i/f (Array.isArray(expectedLegalEntityUuids) && expectedLegalEntityUuids.length > 0) {
        const actualEntityUuids = new Set(rows.map((row) => row.legal_entity_uuid).filter((uuid) => !!uuid));
        const everyExpectedExists = expectedLegalEntityUuids.every((uuid) => actualEntityUuids.has(uuid));
        const hasUnexpected = Array.from(actualEntityUuids).some((uuid) => !expectedLegalEntityUuids.includes(uuid));
        if (!everyExpectedExists || hasUnexpected) {
          return {
            error: true,
            message: this.$t("scale.weighing_on_scale.error_split_legal_entity_mismatch"),
          };
        }
      }*/

      return { error: false };
    },
  },
};
</script>

<style scoped>
.warehouse-split-popup-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.warehouse-split-popup-header {
  flex-shrink: 0;
  padding-bottom: 8px;
}

.warehouse-split-popup-text {
  font-weight: 600;
}

.warehouse-split-popup-grid-wrap {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.warehouse-split-popup-grid-wrap :deep(.dx-datagrid) {
  height: 100%;
}

.warehouse-split-popup-grid-wrap :deep(.dx-datagrid-rowsview) {
  overflow-y: auto;
}

.warehouse-split-popup-actions {
  flex-shrink: 0;
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
